import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { usePeersona } from '../../contexts/peersona-context';
import { ROUTE_PATH } from '../../routes/route-paths';
import { useEligiblePeersonas } from '../../hooks/api/useEligiblePeersonas';
import { AssistantPanelHeader } from '../assistant-widget/assistant-panel-header';
import { PeersonaAvatar } from './peersona-avatar';
import { PeersonaSparkle } from './peersona-sparkle';
import { PEERSONA_TEXT_MUTED } from './peersona-styles';

interface Props {
  /** Kept for source compat with ``PeersonaPanel``; the header no longer
   *  renders anything for it — the strip moved into ``PeersonaWelcomeHero``. */
  onOpenAbout?: () => void;
}

/**
 * Peersona panel header. Wraps the shared ``AssistantPanelHeader`` with a
 * Peersona avatar + name block that doubles as a link to the peer profile.
 *
 * ``listener_role_id`` isn't on ``PeersonaChatBotMeta`` yet, so we resolve
 * it via the env allowlist that ``useEligiblePeersonas`` already loads for
 * the list views (cache shared, no extra fetch).
 */
export const PeersonaHeader: React.FC<Props> = () => {
  const history = useHistory();
  const { chatbot, closePanel } = usePeersona();
  const { peersonas } = useEligiblePeersonas();

  const displayName = chatbot?.display_name || chatbot?.name || 'Peersona';
  const avatar = chatbot?.avatar_file_path ?? null;
  const chatbotId = chatbot?.id ?? null;

  const listenerRoleId = useMemo(() => {
    if (chatbotId == null) return null;
    const match = peersonas.find((p) => p.chatbotId === chatbotId);
    return match?.listenerRoleId ?? null;
  }, [chatbotId, peersonas]);

  const navigateToProfile = () => {
    if (listenerRoleId == null) return;
    history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${listenerRoleId}`);
  };

  const profileLabel = `View ${displayName}'s profile`;
  const profileEnabled = listenerRoleId != null;

  const avatarSlot = (
    <button
      type="button"
      onClick={navigateToProfile}
      disabled={!profileEnabled}
      aria-label={profileLabel}
      className="flex min-w-0 items-center gap-2 rounded-md p-1 text-left transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 disabled:cursor-default disabled:hover:bg-transparent"
    >
      <PeersonaAvatar size={36} src={avatar} name={displayName} />
      <div className="min-w-0">
        <p className="font-albert-sans truncate text-sm font-semibold text-black">{displayName}</p>
        <p
          className="font-albert-sans inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.06em]"
          style={{ color: PEERSONA_TEXT_MUTED }}
        >
          <PeersonaSparkle size={10} />
          Peersona
        </p>
      </div>
    </button>
  );

  return <AssistantPanelHeader mode="chat" avatarSlot={avatarSlot} onClose={closePanel} />;
};
